import { Card, Col, Progress, Row, Statistic, Table, Tag, Typography } from 'antd';
import { ASSET_STAGE_LABEL, percent, type ModelStats, type ProgressReport, type StageCount, type VocabCounts } from '../../lib/progress-types';
import { PROGRESS_LABEL, type VocabProgress } from '../../lib/vocab-types';
import { won } from '../../lib/generation-types';

const PROGRESS_ORDER: VocabProgress[] = ['not_started', 'generating', 'in_review', 'approved_unpublished', 'published'];

/** 구축 현황(요구사항 R1 §14, #96). 제작 완료는 게시 성공 기준 */
export function ProgressTab({ data }: { data: ProgressReport }) {
  const { vocab, assets, models } = data;
  const all = vocab.all;
  const done = all.byProgress.published;
  const typeRows = [
    { key: 'all', name: '전체', ...all },
    { key: 'core', name: '핵심어휘', ...vocab.core },
    { key: 'fringe', name: '주변어휘', ...vocab.fringe },
    ...(vocab.byPurpose
      ? [
          { key: 'default_card', name: '기본 카드 후보', ...vocab.byPurpose.default_card },
          { key: 'search_only', name: '검색 이미지 전용', ...vocab.byPurpose.search_only },
        ]
      : []),
  ];
  const count = (s: string) => assets.byStage.find((x) => x.stage === s);
  const sum = (...stages: string[]) => stages.reduce((n, s) => n + (count(s)?.created ?? 0) + (count(s)?.existing ?? 0), 0);

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} md={8}>
        <Card>
          <Statistic title="어휘 구축 진행률 (게시 완료 / 전체)" value={all.total ? Math.round((done / all.total) * 1000) / 10 : 0} suffix="%" />
          <Progress percent={all.total ? Math.round((done / all.total) * 100) : 0} showInfo={false} />
          <Typography.Text type="secondary">게시 완료 {done} / 전체 {all.total}개</Typography.Text>
        </Card>
      </Col>
      <Col xs={12} md={4}><Card><Statistic title="등록 어휘" value={all.total} suffix="개" /></Card></Col>
      <Col xs={12} md={4}><Card><Statistic title="이미지 미생성 어휘" value={all.byProgress.not_started} suffix="개" /></Card></Col>
      <Col xs={12} md={4}><Card><Statistic title="검수 대기 이미지" value={sum('in_review')} suffix="장" /></Card></Col>
      <Col xs={12} md={4}><Card><Statistic title="게시 실패 이미지" value={sum('publish_failed')} suffix="장" valueStyle={sum('publish_failed') ? { color: '#cf1322' } : undefined} /></Card></Col>

      <Col span={24}>
        <Card title="어휘 현황">
          <Table<(typeof typeRows)[number]>
            size="small"
            pagination={false}
            rowKey="key"
            dataSource={typeRows}
            scroll={{ x: 'max-content' }}
            columns={[
              { title: '구분', dataIndex: 'name' },
              { title: '등록', dataIndex: 'total', align: 'right' },
              ...PROGRESS_ORDER.map((p) => ({
                title: <Tag color={PROGRESS_LABEL[p].color}>{PROGRESS_LABEL[p].label}</Tag>,
                key: p,
                align: 'right' as const,
                render: (_: unknown, r: VocabCounts) => r.byProgress[p],
              })),
              { title: '기본 이미지 게시', dataIndex: 'defaultPublished', align: 'right' },
              { title: '검색용 이미지 게시', dataIndex: 'searchPublished', align: 'right' },
            ]}
          />
          <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
            어휘 관리의 진행 상태와 같은 기준입니다. 연결된 이미지 중 하나라도 게시되면 게시 완료입니다. 재작업만 남은 어휘는 이미지 미생성으로 셉니다.
          </Typography.Paragraph>
        </Card>
      </Col>

      <Col xs={24} lg={12}>
        <Card title="카테고리별 진행률">
          <Table
            size="small"
            pagination={false}
            rowKey="category"
            dataSource={vocab.categories}
            columns={[
              { title: '카테고리', dataIndex: 'label' },
              ...(vocab.categories.some((c) => c.target)
                ? [{
                    title: '기본 카드(앱 / 목표)',
                    key: 'target',
                    align: 'right' as const,
                    render: (_: unknown, c: (typeof vocab.categories)[number]) => (
                      <span title={`관리 중 기본 카드 후보 ${c.defaultCandidates ?? 0}개`}>{c.appCards ?? '-'} / {c.target}</span>
                    ),
                  }]
                : []),
              { title: '핵심', dataIndex: 'core', align: 'right' },
              { title: '주변', dataIndex: 'fringe', align: 'right' },
              { title: '게시 완료 / 전체', key: 'done', align: 'right', render: (_, c) => `${c.published} / ${c.total}` },
              { title: '진행률', key: 'bar', width: 160, render: (_, c) => <Progress size="small" percent={c.total ? Math.round((c.published / c.total) * 100) : 0} /> },
            ]}
          />
          {vocab.categories.some((c) => c.target) && (
            <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
              기본 카드 목표는 카테고리별 6의 배수, 합계 120개입니다(D-V11). '앱'은 지금 배포된 릴리스의 기본 카드 수입니다. 기본 카드에 들지 않는 어휘는 검색 이미지로만 제공합니다.
            </Typography.Paragraph>
          )}
        </Card>
      </Col>

      <Col xs={24} lg={12}>
        <Card title={`이미지 제작 현황 (전체 ${assets.total}장)`}>
          <Table<StageCount>
            size="small"
            pagination={false}
            rowKey="stage"
            dataSource={assets.byStage.filter((s) => s.created + s.existing > 0 || ['in_review', 'approved', 'discarded', 'rework', 'published', 'publish_failed'].includes(s.stage))}
            columns={[
              { title: '단계', dataIndex: 'stage', render: (s: string) => <Tag color={ASSET_STAGE_LABEL[s]?.color}>{ASSET_STAGE_LABEL[s]?.label ?? s}</Tag> },
              { title: `새로 만든 이미지 (${assets.created})`, dataIndex: 'created', align: 'right' },
              { title: `앱 기존 이미지 (${assets.existing})`, dataIndex: 'existing', align: 'right' },
            ]}
          />
          <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
            앱 기존 이미지는 앱에 이미 있던 기본 카드·검색 이미지를 가져온 것입니다. 건수가 0인 진행 단계는 숨깁니다.
          </Typography.Paragraph>
        </Card>
      </Col>

      <Col span={24}>
        <Card title="모델별 통계">
          <Table<ModelStats>
            size="small"
            pagination={false}
            rowKey="modelId"
            dataSource={models}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: '아직 생성한 이미지가 없습니다' }}
            columns={[
              { title: '모델', dataIndex: 'label', render: (l: string, m) => <><div>{l}</div><Typography.Text type="secondary" style={{ fontSize: 12 }}>{m.modelId}</Typography.Text></> },
              { title: '생성', dataIndex: 'generated', align: 'right' },
              { title: '생성 실패', dataIndex: 'failed', align: 'right' },
              { title: '생성 비용', dataIndex: 'costKrw', align: 'right', render: (n: number) => won(n) },
              { title: '승인 / 판정', key: 'approved', align: 'right', render: (_, m) => `${m.approved} / ${m.reviewed}` },
              { title: '승인율', dataIndex: 'approvalRate', align: 'right', render: percent },
              { title: '후처리 필요', key: 'pp', align: 'right', render: (_, m) => `${m.postprocessRequired} (${percent(m.postprocessRate)})` },
              { title: '게시 완료', dataIndex: 'published', align: 'right' },
              { title: '승인 이미지당 비용', dataIndex: 'costPerApprovedKrw', align: 'right', render: (n: number | null) => (n === null ? '-' : won(n)) },
            ]}
          />
          <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
            승인율은 판정을 마친 이미지(승인·폐기·재작업) 중 승인(기본·검색·둘 다) 비율입니다. 후처리 필요 비율은 원본이 저장된 이미지 기준입니다. 비용은 실패한 생성을 포함한 실제 청구액 합계입니다.
          </Typography.Paragraph>
        </Card>
      </Col>
    </Row>
  );
}
